import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonOpenMenu } from "./ButtonOpenMenu";

const LayoutMarginsSection = ({ config, handleInputChange, disabled }) => {
  const { t } = useTranslation();

  // Options prédéfinies pour les marges (en points)
  const marginOptions = [
    {
      value: 20,
      label: `20pt - ${t("sections.layout.marginOptions.veryNarrow")}`,
    },
    { value: 30, label: `30pt - ${t("sections.layout.marginOptions.narrow")}` },
    {
      value: 40,
      label: `40pt - ${t("sections.layout.marginOptions.standard")}`,
    },
    {
      value: 50,
      label: `50pt - ${t("sections.layout.marginOptions.comfortable")}`,
    },
    { value: 60, label: `60pt - ${t("sections.layout.marginOptions.large")}` },
    {
      value: 80,
      label: `80pt - ${t("sections.layout.marginOptions.veryLarge")}`,
    },
    {
      value: 100,
      label: `100pt - ${t("sections.layout.marginOptions.maximum")}`,
    },
  ];

  const textOffsetOptions = [
    { value: 0, label: `0pt - ${t("sections.layout.textOffset.none")}` },
    { value: 4, label: `4pt - ${t("sections.layout.textOffset.minimal")}` },
    { value: 8, label: `8pt - ${t("sections.layout.textOffset.reduced")}` },
    { value: 10, label: `10pt - ${t("sections.layout.textOffset.standard")}` },
    { value: 12, label: `12pt - ${t("sections.layout.textOffset.medium")}` },
    { value: 16, label: `16pt - ${t("sections.layout.textOffset.important")}` },
    { value: 20, label: `20pt - ${t("sections.layout.textOffset.maximum")}` },
  ];
  const preset = [
    {
      name: "Compact",
      topMargin: 30,
      bottomMargin: 30,
      leftMargin: 20,
      rightMargin: 20,
      textOffset: 0,
    },
    {
      name: "Standard",
      topMargin: 50,
      bottomMargin: 50,
      leftMargin: 40,
      rightMargin: 40,
      textOffset: 10,
    },
    {
      name: "Confort",
      topMargin: 80,
      bottomMargin: 80,
      leftMargin: 60,
      rightMargin: 60,
      textOffset: 12,
    },
  ];

  const [isOpen, setIsOpen] = useState(false);

  let selectedPreset = "none";
  for (const presetItem of preset) {
    if (
      config.topMargin === presetItem.topMargin &&
      config.bottomMargin === presetItem.bottomMargin &&
      config.leftMargin === presetItem.leftMargin &&
      config.rightMargin === presetItem.rightMargin &&
      config.textOffset === presetItem.textOffset
    ) {
      selectedPreset = presetItem.name.toLowerCase();
      break;
    }
  }

  const findPreset = (presetName) => {
    return preset.find(
      (p) => p.name.toLowerCase() === presetName.toLowerCase()
    );
  };

  const changePreset = (presetName) => {
    const presetData = findPreset(presetName);
    if (presetData) {
      handleInputChange("multipleFields", presetData);
    } else {
      console.error(`Preset ${presetName} not found`);
    }
  };

  const standard = findPreset("Standard");

  return (
    <div className="param-group">
      <div className="param-group-header">
        <h3 className="param-group-title">
          <span>📐</span>
          {t("sections.layout.title")}
        </h3>
        <ButtonOpenMenu isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>

      <div
        className={clsx("grid grid-cols-1 gap-4 mt-2 md:grid-cols-2", {
          hidden: !isOpen,
        })}
      >
        <div className="form-group">
          <label className="form-label" htmlFor="topMargin">
            📏 {t("sections.layout.top")}
          </label>
          <select
            id="topMargin"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.topMargin || standard.topMargin}
            onChange={(e) =>
              handleInputChange("topMargin", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {marginOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="bottomMargin">
            📏 {t("sections.layout.bottom")}
          </label>
          <select
            id="bottomMargin"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.bottomMargin || standard.bottomMargin}
            onChange={(e) =>
              handleInputChange("bottomMargin", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {marginOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="leftMargin">
            ⬅️ {t("sections.layout.left")}
          </label>
          <select
            id="leftMargin"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.leftMargin || standard.leftMargin}
            onChange={(e) =>
              handleInputChange("leftMargin", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {marginOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="rightMargin">
            ➡️ {t("sections.layout.right")}
          </label>
          <select
            id="rightMargin"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={config.rightMargin || standard.rightMargin}
            onChange={(e) =>
              handleInputChange("rightMargin", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {marginOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-2 form-group">
          <label className="form-label" htmlFor="textOffset">
            🔤 {t("sections.layout.textOffsetLabel")}
          </label>
          <select
            id="textOffset"
            className={clsx("form-input", {
              "opacity-50 cursor-not-allowed": disabled,
              "hover:border-primary-400 focus:border-primary-500": !disabled,
            })}
            value={
              config.textOffset === undefined
                ? standard.textOffset
                : config.textOffset
            }
            onChange={(e) =>
              handleInputChange("textOffset", parseInt(e.target.value))
            }
            disabled={disabled}
          >
            {textOffsetOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Présets rapides */}
      <div className="p-3 mt-4 rounded-lg border border-primary-200 bg-background-50">
        <div className="mb-2 text-sm font-medium text-text-700">
          ⚡ {t("sections.layout.presets")} : {selectedPreset}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "compact"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-secondary-300 bg-secondary-100 text-secondary-700",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-secondary-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset !== "compact",
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "compact",
              }
            )}
            onClick={() => {
              changePreset("Compact");
            }}
            disabled={disabled}
          >
            📄 {t("sections.layout.presets.compact")}
          </button>
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "standard"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-primary-300 bg-primary-100 text-primary-700",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-primary-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset !== "standard",
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "standard",
              }
            )}
            onClick={() => {
              changePreset("Standard");
            }}
            disabled={disabled}
          >
            📋 {t("sections.layout.presets.standard")}
          </button>
          <button
            type="button"
            className={clsx(
              "px-2 py-1 text-xs border transition-all duration-200 btn",
              selectedPreset === "confort"
                ? "border-accent-500 bg-accent-100 text-accent-800 shadow-lg ring-2 ring-accent-300"
                : "border-accent-300 bg-accent-100 text-accent-700",
              {
                "opacity-50 cursor-not-allowed": disabled,
                "hover:bg-accent-200 hover:scale-105 active:scale-95":
                  !disabled && selectedPreset === "confort",
              }
            )}
            onClick={() => {
              changePreset("Confort");
            }}
            disabled={disabled}
          >
            🎨 {t("sections.layout.presets.confort")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LayoutMarginsSection;
